import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Maximize, Maximize2, Minimize2, ZoomIn, ZoomOut } from 'lucide-react';
import { Button } from '../components/button';
import { Skeleton } from '../components/skeleton';
import { cn } from '../lib/cn';
import { GenealogyNode } from './GenealogyNode';
import { usePanZoom } from './usePanZoom';
import type {
  AddSlotContext,
  GenealogyNodeFields,
  GenealogyTreeNode,
  GenealogyTreeView,
} from './types';

export type GenealogyTreeProps = {
  nodes: GenealogyTreeNode[];
  rootSlotId: string;
  view: GenealogyTreeView;
  currency: string;
  onAddSlot: (ctx: AddSlotContext) => void;
  isLoading?: boolean;
  defaultExpandedDepth?: number;
  emptyMessage?: string;
  height?: number;
  /** Slot-id chain (root→target) to force-expand, e.g. a search result. */
  expandPath?: string[];
  /** Slot to ring + center in view (e.g. a search match). */
  highlightSlotId?: string;
  /** When false, the "Add New Slot" action card is hidden (tenant toggle). Default true. */
  enableSlotActivation?: boolean;
  /** When true, the binary L/R empty seats render as muted, non-interactive
   * placeholders (set when the tenant's Auto Place is on). The seats are always
   * rendered so L/R geometry never collapses. Default false. */
  binaryPlacementReadOnly?: boolean;
  /**
   * Called when a node with downline (childCount > 0) whose children aren't
   * loaded yet is expanded. The page deepens the fetch one level. Enables
   * lazy, level-by-level loading instead of a manual "Load deeper" button.
   */
  onLoadChildren?: (node: GenealogyTreeNode) => void;
  /**
   * Which detail fields the binary node card shows (tenant admin setting).
   * Applies to the binary view only; omit for unilevel. When omitted the card
   * falls back to owner + slot code (today's behavior).
   */
  nodeFields?: GenealogyNodeFields;
};

export function GenealogyTree({
  nodes,
  rootSlotId,
  view,
  currency,
  onAddSlot,
  isLoading = false,
  defaultExpandedDepth = 2,
  emptyMessage = 'Nobody under this slot yet.',
  height = 600,
  expandPath,
  highlightSlotId,
  enableSlotActivation = true,
  binaryPlacementReadOnly = false,
  onLoadChildren,
  nodeFields,
}: GenealogyTreeProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { scale, pan, isInteracting, reset, zoomBy, fit, centerOn, step } = usePanZoom({
    viewportRef,
    contentRef,
  });
  const [fullscreen, setFullscreen] = useState(false);
  // Keep the tree hidden until the first center runs so the initial paint is
  // already centered (no flash of a top-left-anchored tree).
  const [ready, setReady] = useState(false);

  const { root, childrenByParent } = useMemo(() => {
    const byParent = new Map<string, GenealogyTreeNode[]>();
    let rootNode: GenealogyTreeNode | null = null;
    for (const n of nodes) {
      if (n.slotId === rootSlotId) {
        rootNode = n;
        continue;
      }
      const key = n.parentSlotId ?? '';
      const arr = byParent.get(key);
      if (arr) arr.push(n);
      else byParent.set(key, [n]);
    }
    return { root: rootNode, childrenByParent: byParent };
  }, [nodes, rootSlotId]);

  const childrenForNode = (slotId: string) => childrenByParent.get(slotId) ?? [];

  const [expanded, setExpanded] = useState<Map<string, boolean>>(() => new Map());
  const seededTreeRef = useRef<string>('');

  // Seed default-depth expansions, but preserve user toggles across lazy-load refetches.
  // A full reset happens only when the tree identity (root or view) changes; otherwise we
  // merge — adding defaults for newly-arrived nodes without clobbering what the user opened.
  useEffect(() => {
    const treeKey = `${rootSlotId}|${view}`;
    const isNewTree = seededTreeRef.current !== treeKey;
    setExpanded((prev) => {
      const next = isNewTree ? new Map<string, boolean>() : new Map(prev);
      for (const n of nodes) {
        if (!next.has(n.slotId) && n.depth < defaultExpandedDepth) next.set(n.slotId, true);
      }
      next.set(rootSlotId, true);
      return next;
    });
    seededTreeRef.current = treeKey;
  }, [nodes, rootSlotId, view, defaultExpandedDepth]);

  // Initial centering — once per tree identity (root+view). Transform-based, so
  // it survives keepPreviousData refetches (no `isLoading` in deps, which is
  // what used to fight the search centering below). Skipped while a search
  // target is active so Fix 3 owns positioning.
  const centeredKeyRef = useRef('');
  useEffect(() => {
    if (!root) return;
    if (highlightSlotId) return;
    const key = `${root.slotId}|${view}`;
    if (centeredKeyRef.current === key) return;
    const el = viewportRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      const rootEl = el.querySelector(`[data-slot-id="${root.slotId}"]`);
      if (!rootEl) return;
      centerOn(rootEl, { mode: 'top' });
      centeredKeyRef.current = key;
      setReady(true);
    });
    return () => cancelAnimationFrame(raf);
  }, [root?.slotId, view, highlightSlotId, centerOn]);

  // Safety: never leave the tree hidden (e.g. deep-linked straight to a search
  // target, where the initial-center effect early-returns).
  useEffect(() => {
    if (ready) return;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setReady(true)));
    return () => cancelAnimationFrame(raf);
  }, [ready]);

  // Force-expand a search-result path.
  const expandPathKey = (expandPath ?? []).join(',');
  useEffect(() => {
    if (!expandPath || expandPath.length === 0) return;
    setExpanded((prev) => {
      const next = new Map(prev);
      for (const id of expandPath) next.set(id, true);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandPathKey]);

  // Center on the matched node once the deeper data has arrived AND the path has
  // expanded it into the DOM. Re-runs on data arrival (`targetLoaded`) and on
  // each expansion (`expanded`) so the async depth-bump from jumpTo is handled —
  // but centers only ONCE per target (tracked in `centeredHighlightRef`), so a
  // later unrelated caret toggle doesn't yank the view back to the old match.
  const targetLoaded = !!highlightSlotId && nodes.some((n) => n.slotId === highlightSlotId);
  const centeredHighlightRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!highlightSlotId) {
      centeredHighlightRef.current = undefined; // search cleared → allow a fresh center
      return;
    }
    if (centeredHighlightRef.current === highlightSlotId) return; // already centered this target
    if (!targetLoaded) return;
    const el = viewportRef.current;
    if (!el) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      // Two frames: let the expansion commit and lay out before measuring.
      raf2 = requestAnimationFrame(() => {
        const target = el.querySelector(`[data-slot-id="${highlightSlotId}"]`);
        if (!target) return; // not in the DOM yet — a later dep change re-fires
        centerOn(target, { mode: 'center', scale: Math.min(Math.max(scale, 0.7), 1) });
        centeredHighlightRef.current = highlightSlotId; // done — don't re-center on expansions
        setReady(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
    // `scale` is read fresh for readability normalization but intentionally not a
    // dep — including it would re-fire this effect on every centerOn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightSlotId, expandPathKey, targetLoaded, expanded, centerOn]);

  const nodeById = useMemo(() => {
    const m = new Map<string, GenealogyTreeNode>();
    for (const n of nodes) m.set(n.slotId, n);
    return m;
  }, [nodes]);

  const toggle = (slotId: string) => {
    const willExpand = !(expanded.get(slotId) ?? false);
    // Lazy-load: expanding a node that has downline but whose children aren't
    // in the loaded set yet → ask the page to fetch the next level.
    if (willExpand && onLoadChildren) {
      const node = nodeById.get(slotId);
      if (node && node.childCount > 0 && childrenForNode(slotId).length === 0) {
        onLoadChildren(node);
      }
    }
    setExpanded((prev) => {
      const next = new Map(prev);
      next.set(slotId, willExpand);
      return next;
    });
  };

  const toggleFullscreen = () => {
    setFullscreen((f) => !f);
    // Recenter for the new viewport size after the layout settles.
    requestAnimationFrame(() => requestAnimationFrame(() => reset()));
  };

  return (
    <div
      ref={viewportRef}
      className={cn(
        'relative select-none touch-none overflow-hidden cursor-grab',
        fullscreen
          ? 'fixed inset-0 z-50 h-screen w-screen rounded-none border-0 bg-background'
          : 'rounded-lg border border-border/40 bg-muted/20',
      )}
      style={fullscreen ? undefined : { height }}
    >
      {isLoading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : !root ? (
        <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <>
          <div
            ref={contentRef}
            className="origin-top-left p-6 will-change-transform"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              transformOrigin: '0 0',
              transition: isInteracting ? 'none' : 'transform 0.25s ease',
              visibility: ready ? 'visible' : 'hidden',
            }}
          >
            <ul className="mlm-tree">
              <GenealogyNode
                node={root}
                view={view}
                currency={currency}
                childrenForNode={childrenForNode}
                expanded={expanded}
                onToggle={toggle}
                onAddSlot={onAddSlot}
                isRoot
                highlightSlotId={highlightSlotId}
                enableSlotActivation={enableSlotActivation}
                binaryPlacementReadOnly={binaryPlacementReadOnly}
                nodeFields={nodeFields}
              />
            </ul>
          </div>

          <div className="absolute bottom-3 right-3 z-10 flex items-center gap-1 rounded-lg border border-border bg-background/80 p-1 shadow-sm backdrop-blur">
            <Button
              variant="ghost"
              size="icon"
              data-interactive
              aria-label="Zoom out"
              className="h-8 w-8"
              onClick={() => zoomBy(-step)}
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-interactive
              aria-label="Reset zoom"
              className="h-8 min-w-[3rem] px-2 font-mono text-xs"
              onClick={reset}
            >
              {Math.round(scale * 100)}%
            </Button>
            <Button
              variant="ghost"
              size="icon"
              data-interactive
              aria-label="Zoom in"
              className="h-8 w-8"
              onClick={() => zoomBy(step)}
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              data-interactive
              aria-label="Fit to view"
              className="h-8 w-8"
              onClick={fit}
            >
              <Maximize className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              data-interactive
              aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
              className="h-8 w-8"
              onClick={toggleFullscreen}
            >
              {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function GenealogyTreeSkeleton({ height = 600 }: { height?: number }) {
  return <Skeleton className="w-full" style={{ height }} />;
}
